<template>
	<div ref="el" data-component="faq" data-page="Faq.page">
		<div class="faq-content-shell">
			<span ref="backgroundEl" class="inner-bg" aria-hidden="true">
				<span class="fade" />
			</span>

			<Header v-bind="headerProps" />

			<main>
				<section class="faq-items-section">
					<div class="g-row">
						<div class="g-col xxl-8 xxl-offset-7 xs-14 xs-offset-1">
							<FaqItems v-bind="faqItemsSection" />
						</div>
					</div>
				</section>
			</main>

			<div v-if="hasSideCta" class="faq-cta-mobile">
				<div class="g-row">
					<div class="g-col xs-14 xs-offset-1">
						<div class="mobile-call-to-action">
							<p v-if="sideCta.title" class="-headline-5 title">{{ sideCta.title }}</p>
							<Button v-if="sideCta.cta" v-bind="sideCta.cta" tone="primary" />
						</div>
					</div>
				</div>
			</div>

			<div v-if="hasSideCta" class="faq-cta-rail">
				<div class="g-row rail-row">
					<div class="g-col xxl-3 xs-12 xs-offset-1 rail-col">
						<div class="sticky-call-to-action">
							<p v-if="sideCta.title" class="-headline-5 title">{{ sideCta.title }}</p>
							<Button v-if="sideCta.cta" v-bind="sideCta.cta" tone="primary" />
						</div>
					</div>
				</div>
			</div>
		</div>

		<Footer v-bind="footer" />
	</div>
</template>

<script setup>
import { gsap } from 'gsap'

import { Header } from './sections'
import { FaqItems } from '~/components/fragments'
import { Footer } from '~/components/sections'
import { Button } from '~/components/primitives'

const props = defineProps({
	sections: { type: Array, default: undefined },
	footer: { type: Object, default: undefined },
})

const el = ref(null)
const backgroundEl = ref(null)
const pageContext = inject('pageContext')
let backgroundTween = null

const sections = computed(() => (Array.isArray(props.sections) ? props.sections : []))
const getSection = (type) => sections.value.find((section) => section?._type === type)
const headerSection = computed(() => getSection('header'))
const faqItemsSection = computed(() => getSection('faqItems'))
const sideCta = computed(() => headerSection.value?.sideCta || {})
const hasSideCta = computed(() => Boolean(sideCta.value?.title || sideCta.value?.cta))
const headerProps = computed(() => {
	if (!headerSection.value) return undefined

	const { sideCta: _sideCta, ...header } = headerSection.value
	return header
})

const setup = () => {
	if (!import.meta.client || !backgroundEl.value) return
	gsap.set(backgroundEl.value, { scale: 0.82, transformOrigin: '50% 0%', autoAlpha: 0 })
}

const init = () => {
	if (!import.meta.client || !backgroundEl.value) return

	backgroundTween = gsap.to(backgroundEl.value, {
		scale: 1,
		autoAlpha: 1,
		duration: 1.1,
		ease: 'power3.out',
	})
}

onBeforeUnmount(() => {
	backgroundTween?.kill()
	backgroundTween = null
})

onMounted(() => {
	const loader = pageContext?.$page?.loader
	setup()

	if (!loader) {
		init()
		return
	}

	loader.ready.then(() => init())
})
</script>

<style lang="scss" scoped>
[data-component='faq'] {
	position: relative;

	.faq-content-shell {
		position: relative;
		padding-bottom: rs(150, 72);
	}

	.inner-bg {
		position: absolute;
		inset: rs(16, 12);
		z-index: 0;
		overflow: hidden;
		border-radius: rs(34, 24);
		background-color: var(--color-river-blue-50);
		transform: scale(0.82);
		transform-origin: 50% 0%;
		opacity: 0;
		visibility: hidden;
		will-change: transform, opacity;

		.fade {
			position: absolute;
			bottom: 0;
			width: 100%;
			height: rs(450, 320);
			background: linear-gradient(0deg, var(--color-geral-white) 15%, rgba(255, 255, 255, 0) 100%);
		}
	}

	main,
	.faq-cta-mobile,
	.faq-cta-rail {
		position: relative;
		z-index: 2;
	}

	main {
		margin-top: rs(180, 92);
	}

	.faq-items-section {
		position: relative;
	}

	.faq-cta-mobile {
		display: none;
		visibility: hidden;
		pointer-events: none;
	}

	.faq-cta-rail {
		position: absolute;
		inset: 0 0 rs(150, 72) 0;
		pointer-events: none;

		.rail-row,
		.rail-col {
			height: 100%;
		}

		.rail-row {
			align-items: stretch;
		}

		.rail-col {
			display: flex;
			flex-direction: column;
		}

		.sticky-call-to-action {
			position: sticky;
			bottom: rs(24, 24);
			width: column-width(3);
			margin-top: auto;
			pointer-events: auto;

			.title {
				margin-bottom: rs(15, 15);
				color: var(--color-river-blue-900);
			}
		}
	}

	@include max-width($sm) {
		.faq-cta-rail {
			display: none;
			visibility: hidden;
			pointer-events: none;
		}
	}

	@include max-width($xs) {
		.faq-cta-mobile {
			display: block;
			visibility: visible;
			pointer-events: auto;
			margin-top: rs(56, 56);

			.mobile-call-to-action {
				display: flex;
				flex-direction: column;
				align-items: flex-start;

				.title {
					max-width: column-width(10, true);
					margin-bottom: rs(15, 15);
					color: var(--color-river-blue-900);
				}
			}
		}
	}
}
</style>
